import { useState, useEffect, createContext, useContext } from "react";
import type { User, Session, SupabaseClient } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function getSupabase(): Promise<SupabaseClient> {
  const { supabase } = await import("@/integrations/supabase/client");
  return supabase;
}

/**
 * Auth boots after idle so the homepage critical path doesn't parse/eval
 * the Supabase client (~170 KB) during first paint.
 */
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAdminStatus = async (client: SupabaseClient, userId: string) => {
    try {
      const { data, error } = await client
        .from("admin_users")
        .select("id")
        .eq("user_id", userId)
        .single();

      if (error) {
        setIsAdmin(false);
        return;
      }

      setIsAdmin(!!data);
    } catch {
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const boot = async () => {
      try {
        const client = await getSupabase();
        if (cancelled) return;

        const {
          data: { subscription },
        } = client.auth.onAuthStateChange(async (_event, nextSession) => {
          if (cancelled) return;
          setSession(nextSession);
          setUser(nextSession?.user ?? null);

          if (nextSession?.user) {
            setTimeout(() => checkAdminStatus(client, nextSession.user.id), 0);
          } else {
            setIsAdmin(false);
          }

          setIsLoading(false);
        });
        unsubscribe = () => subscription.unsubscribe();

        const {
          data: { session: initialSession },
        } = await client.auth.getSession();
        if (cancelled) return;

        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        if (initialSession?.user) {
          await checkAdminStatus(client, initialSession.user.id);
        }

        setIsLoading(false);
      } catch {
        if (!cancelled) setIsLoading(false);
      }
    };

    // Admin/auth routes need session sooner; marketing pages can wait for idle
    const path = window.location.pathname;
    const needsAuthSoon =
      path.startsWith("/admin") ||
      path.startsWith("/suscribirse") ||
      path.startsWith("/suscripcion");

    if (needsAuthSoon) {
      void boot();
    } else if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(() => void boot(), { timeout: 6000 });
    } else {
      timeoutId = setTimeout(() => void boot(), 3000);
    }

    return () => {
      cancelled = true;
      unsubscribe?.();
      if (idleId !== undefined && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const client = await getSupabase();
    const { error } = await client.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const client = await getSupabase();
    const { error } = await client.auth.signUp({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    const client = await getSupabase();
    await client.auth.signOut();
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin,
        isLoading,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
